import React from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, TrendingUp } from "lucide-react";
import { getSupabaseProductImageUrl } from "../../../../utils/imageUtils";
import type { Product } from "../Types";
import WishlistButton from "../../../WishlistButton";
import QuickView from "../QuickView";

interface ProductCardProps {
  product: Product;

  adding?: boolean;
  onAddToCart?: () => void;
  isWishlisted?: boolean;
  isShowWishlist?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = React.memo(
  ({
    product,
    adding = false,
    onAddToCart,
    isWishlisted,
    isShowWishlist = false,
  }) => {
    const navigate = useNavigate();
    const [showQuickView, setShowQuickView] = React.useState(false);

    // Handle new string[] format or old {path: string}[] format
    const images = product.images || [];
    const lastImageEntry = images[images.length - 1];
    const lastImage =
      typeof lastImageEntry === "string"
        ? lastImageEntry
        : (lastImageEntry as any)?.path;
    const imageUrl = getSupabaseProductImageUrl(lastImage);

    // Use the pre-calculated prices from the hook transformations
    // If on sale show regular price
    const displayPrice = product.price ?? 0;
    const originalPrice = product.original_price ?? product.price;
    const saleRate = product.sale_rate ?? product.discount ?? 0;
    const isOnSale = product.on_sale;

    // Keyboard accessibility for card
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        navigate(`/product/${product.id}`);
      }
    };

    return (
      <div
        className="group shadow-sm border border-neutral-900/90 bg-neutral-900 rounded-lg overflow-hidden hover:shadow-xl cursor-pointer h-105 flex flex-col transition-all"
        tabIndex={0}
        role="button"
        aria-label={`View details for ${product.name}`}
        onClick={(e) => {
          // Prevent card click if QuickView is open
          if (showQuickView) {
            e.stopPropagation();
            return;
          }
          navigate(`/product/${product.id}`);
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Image */}
        <div className="relative h-52 bg-neutral-800/90 shrink-0">
          <div className="absolute z-20 w-6 top-3 right-4 flex flex-col gap-2 items-center pointer-events-auto">
            {isShowWishlist && (
              <WishlistButton
                productId={product.id}
                isWishlisted={isWishlisted}
                size="md"
                className="bg-white/90 backdrop-blur-sm shadow-md w-10 h-10 flex items-center justify-center"
              />
            )}
          </div>
          <div className="absolute top-3 left-3 flex flex-col gap-2 z-10 text-center">
            {/* Feature Badges - Shown separately */}
            {product.sortType === "featured" && (
              <span className="bg-primary text-black text-xs font-semibold px-2.5 py-1 rounded shadow-sm">
                Featured
              </span>
            )}
            {product.is_new && (
              <span className="bg-emerald-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-sm">
                NEW
              </span>
            )}
            {product.trending && (
              <span className="bg-purple-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-sm flex items-center gap-1">
                <TrendingUp size={12} />
                HOT
              </span>
            )}
            {/* Sale Rate Badge - Always shown if product is on sale */}
            {isOnSale && saleRate > 0 && (
              <span className="bg-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded shadow-sm">
                -{saleRate}%
              </span>
            )}
          </div>
          {/* Quick View Button overlay on hover */}
          <div className="absolute inset-0 flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              className="bg-neutral-800/50 text-primary font-semibold px-4 py-2 rounded-lg shadow-lg border border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              tabIndex={0}
              aria-label={`Quick view for ${product.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowQuickView(true);
              }}
            >
              Quick View
            </button>
          </div>

          <div className="w-full h-full flex items-center justify-center p-2">
            <img
              src={imageUrl || product.image || "/assets/images/no-image.png"}
              alt={
                product.name ? `${product.name} product image` : "Product image"
              }
              className="object-contain w-full h-full group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        </div>

        {/* Details */}
        <div className="p-4 flex flex-col flex-1">
          <div className="flex-1">
            {product.brand_name && (
              <div className="text-xs text-neutral-400 mb-1.5 uppercase tracking-wide">
                {product.brand_name}
              </div>
            )}
            <h3 className="font-semibold text-white mb-2 line-clamp-2 text-sm group-hover:text-primary-light transition-colors">
              {product.name}
            </h3>
            {/* Static 5-star rating */}
            <div className="flex gap-0.5 mb-2" aria-label="Rating: 5 out of 5">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  aria-hidden="true"
                  className="w-4 h-4 text-primary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <title>Filled star</title>
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.921-.755 1.688-1.54 1.118l-3.38-2.455a1 1 0 00-1.175 0l-3.38 2.455c-.784.57-1.838-.197-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.174 9.393c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.966z" />
                </svg>
              ))}
            </div>
            {/* {product.description && (
              <p className="text-xs text-gray-600 mb-3 line-clamp-1">
                {product.description}
              </p>
            )} */}
            <div className="mb-3">
              <span className="text-xl font-bold text-white">
                ${displayPrice.toFixed(2)}
              </span>
              {isOnSale && originalPrice && originalPrice > displayPrice && (
                <span className="text-sm text-gray-400 line-through ml-2">
                  ${originalPrice.toFixed(2)}
                </span>
              )}
            </div>
          </div>

          <button
            className="w-full text-black bg-primary hover:bg-primary-dark rounded-lg py-2.5 px-4 flex items-center justify-center gap-2 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              filter: adding ? "brightness(90%)" : "brightness(100%)",
            }}
            onMouseEnter={(e) => {
              if (!adding) {
                (e.currentTarget as HTMLElement).style.filter =
                  "brightness(110%)";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.filter = adding
                ? "brightness(90%)"
                : "brightness(100%)";
            }}
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart?.();
            }}
            disabled={adding}
            aria-label={adding ? "Adding to cart" : "Add to cart"}
          >
            <ShoppingCart
              className={`w-4 h-4 ${adding ? "animate-pulse" : "group-hover:scale-110"} transition-transform`}
            />
            <span className="text-sm font-semibold">
              {adding ? "Adding..." : "Add To Cart"}
            </span>
          </button>
        </div>
        {/* Quick View Modal */}
        {showQuickView && (
          <QuickView
            product={product}
            discountedPrice={displayPrice}
            imageUrl={imageUrl || ""}
            onAddToCart={onAddToCart}
            onClose={() => setShowQuickView(false)}
          />
        )}
      </div>
    );
  },
);
export { ProductCard };
